import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Flag, Image as ImageIcon, Plus, X } from 'lucide-react';
import { db } from '@/lib/db';
import { processAchievementsAfterRound } from '@/lib/achievements/process';
import type { Course, CourseHole, CourseTee, HoleRange, Member, MemberPublic, PostMetadataGame } from '@/lib/db/types';
import { courseHandicap } from '@/lib/courses/group';
import { uploadFile, deleteUploadedFile } from '@/lib/storage';
import { callShotSettleMessage } from '@/lib/shots/call-shot-settle';
import { Sheet } from '@/components/shared/Sheet';
import { ImageCropper } from '@/components/shared/ImageCropper';
import { CourseTabPicker } from '@/components/shared/CourseTabPicker';
import { EmojiPicker } from '@/components/shared/EmojiPicker';
import { GroupScorecard, type GroupScorecardPlayer } from '@/components/play/GroupScorecard';
import { GamesEditor } from '@/components/feed/GamesEditor';
import { LiveBetsPanel, type ComposeLiveBet } from '@/components/feed/LiveBetsPanel';
import { gamesWithSettlement, settledLiveBets } from '@/lib/feed/post-settlement';
import { playedPar } from '@/lib/scoring/played-par';
import { isValidRoundGross } from '@/lib/scoring/round-gross';

type ComposeMode = 'photo' | 'quick' | 'full';
type LocalPhoto = { blob: Blob; previewUrl: string; filename?: string };

interface Props {
  member: Member;
  initialMode?: 'quick' | 'photo' | 'full';
  onClose: () => void;
  onPublished: () => void;
  onRoundPublished?: (postId: string) => void;
  onCallSettled?: (message: string) => void;
  onAchievement?: (achievements: Awaited<ReturnType<typeof processAchievementsAfterRound>>) => void;
  onAce?: (holeNum: number, courseName: string) => void;
}


function rangeIndices(holes: HoleRange, total: number): number[] {
  if (holes === 'front-9') return Array.from({ length: Math.min(9, total) }, (_, i) => i);
  if (holes === 'back-9') return Array.from({ length: Math.min(9, total - 9) }, (_, i) => i + 9);
  return Array.from({ length: Math.min(18, total) }, (_, i) => i);
}

function emptyScores(len: number): (number | null)[] {
  return Array(len).fill(null);
}

export function ComposeRound({
  member,
  initialMode = 'quick',
  onClose,
  onPublished,
  onRoundPublished,
  onCallSettled,
  onAchievement,
  onAce,
}: Props) {
  const [mode, setMode] = useState<ComposeMode>(
    initialMode === 'photo' ? 'photo' : initialMode === 'full' ? 'full' : 'quick',
  );
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseHoles, setCourseHoles] = useState<CourseHole[]>([]);
  const [tees, setTees] = useState<CourseTee[]>([]);
  const [allMembers, setAllMembers] = useState<MemberPublic[]>([]);
  const [courseId, setCourseId] = useState('');
  const [teeId, setTeeId] = useState('');
  const [holes, setHoles] = useState<HoleRange>('full');
  const [quickScore, setQuickScore] = useState('');
  const [holeScores, setHoleScores] = useState<(number | null)[]>(emptyScores(18));
  const [opponentScores, setOpponentScores] = useState<Record<string, (number | null)[]>>({});
  const [puttsById, setPuttsById] = useState<Record<string, (number | null)[]>>({});
  const [smack, setSmack] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [tagged, setTagged] = useState<string[]>([]);
  const [guestName, setGuestName] = useState('');
  const [guests, setGuests] = useState<Array<{ name: string; handicap?: number }>>([]);
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const [videos, setVideos] = useState<LocalPhoto[]>([]);
  const [composeGames, setComposeGames] = useState<PostMetadataGame[]>([]);
  const [liveBets, setLiveBets] = useState<ComposeLiveBet[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([
      db.courses.list().then((data) => {
        setCourses(data);
        if (data[0]) setCourseId(data[0].id);
      }),
      db.members.list({ limit: 100 }).then(setAllMembers),
    ]);
  }, []);

  useEffect(() => {
    if (!courseId) return;
    void Promise.all([
      db.courses.tees(courseId).then((data) => {
        setTees(data);
        const white = data.find((t) => t.name === 'White') || data[0];
        if (white) setTeeId(white.id);
      }),
      db.courses.holes(courseId).then(setCourseHoles),
    ]);
  }, [courseId]);

  useEffect(() => {
    setHoleScores(emptyScores(18));
    setOpponentScores({});
    setPuttsById({});
  }, [holes, courseId]);

  const course = courses.find((c) => c.id === courseId);
  const tee = tees.find((t) => t.id === teeId);
  const playedHoles = useMemo(() => {
    const sorted = [...courseHoles].sort((a, b) => a.hole_num - b.hole_num);
    const idx = rangeIndices(holes, sorted.length);
    return idx.map((i) => sorted[i]).filter(Boolean);
  }, [courseHoles, holes]);

  const fullGross = useMemo(
    () => playedHoles
      .map((h) => holeScores[h.hole_num - 1])
      .filter((s): s is number => typeof s === 'number' && Number.isFinite(s))
      .reduce((a, b) => a + b, 0),
    [holeScores, playedHoles],
  );

  const fullComplete = useMemo(
    () => playedHoles.every((h) => {
      const s = holeScores[h.hole_num - 1];
      return typeof s === 'number' && Number.isFinite(s);
    }),
    [holeScores, playedHoles],
  );
  const players: GroupScorecardPlayer[] = useMemo(() => [
    {
      id: member.id,
      name: member.name,
      handicap: member.handicap,
      you: true,
      avatar: member.avatar,
      profile_photo_url: member.profile_photo_url,
    },
    ...tagged.map((id) => {
      const m = allMembers.find((x) => x.id === id);
      return {
        id,
        name: m?.name || 'Member',
        handicap: m?.handicap,
        avatar: m?.avatar,
        profile_photo_url: m?.profile_photo_url,
      };
    }),
    ...guests.map((g, i) => ({
      id: `guest-${i}`,
      name: g.name,
      handicap: g.handicap,
      guest: true,
    })),
  ], [member, tagged, guests, allMembers]);

  const canPublish = mode === 'photo'
    ? !!(smack.trim() || photos.length || videos.length)
    : mode === 'quick'
      ? !!quickScore && isValidRoundGross(parseInt(quickScore, 10), holes) && !!courseId
      : fullComplete && fullGross > 0 && isValidRoundGross(fullGross, holes) && !!courseId;

  const addGuest = () => {
    if (!guestName.trim()) return;
    setGuests((g) => [...g, { name: guestName.trim() }]);
    setGuestName('');
  };

  const publishRound = async (gross: number, scores?: (number | null)[]): Promise<string> => {
    if (!courseId) throw new Error('Please select a course');

    const metadataGames: PostMetadataGame[] = gamesWithSettlement(composeGames);
    const settledBets = settledLiveBets(liveBets);

    const hole_scores = scores
      ? playedHoles
          .map((h) => {
            const strokes = scores[h.hole_num - 1];
            if (typeof strokes !== 'number' || !Number.isFinite(strokes)) return null;
            const putts = puttsById[member.id]?.[h.hole_num - 1];
            const row: { hole_num: number; strokes: number; putts?: number } = {
              hole_num: h.hole_num,
              strokes,
            };
            if (typeof putts === 'number' && Number.isFinite(putts)) row.putts = putts;
            return row;
          })
          .filter((h): h is { hole_num: number; strokes: number; putts?: number } => h !== null)
      : undefined;

    const round = await db.rounds.create({
      course_id: courseId,
      tee_id: teeId || undefined,
      holes,
      gross_total: gross,
      notes: smack.trim() || undefined,
      hole_scores,
    });

    if (tagged.length) await db.roundTags.set(round.id, tagged);

    const post = await db.posts.create({
      type: 'round',
      round_id: round.id,
      body: smack.trim() || `Posted ${gross} at ${course?.name || 'the course'}.`,
      metadata: {
        tagged,
        guests,
        ...(metadataGames.length ? { games: metadataGames } : {}),
        ...(settledBets.length ? {
          liveBets: settledBets.map((b) => ({
            id: b.id,
            type: b.type,
            terms: b.terms,
            wager_cents: b.wager_cents,
            status: b.status,
          })),
        } : {}),
        courseName: course?.name,
        ...(course?.slug ? { courseSlug: course.slug } : {}),
        teeName: tee?.name,
      },
    });

    try {
      const unlocked = await processAchievementsAfterRound(round.id);
      if (unlocked.length) onAchievement?.(unlocked);
    } catch {
      // Achievement processing should not block a successful post
    }

    try {
      const settle = await db.callShots.tryAutoSettleAfterRoundPost({
        course_id: courseId,
        gross_total: gross,
        holes,
        played_at: round.played_at,
      });
      if (settle.settled && settle.targetScore != null && settle.actualScore != null && settle.points != null) {
        onCallSettled?.(callShotSettleMessage(settle.targetScore, settle.actualScore, settle.points));
      }
    } catch {
      // Call settle should not block a successful post
    }

    return post.id;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canPublish || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'photo') {
        const mediaUrls: string[] = [];
        try {
          for (let i = 0; i < photos.length; i += 1) {
            const url = await uploadFile('post-photos', photos[i].blob, `post-${i + 1}.jpg`);
            mediaUrls.push(url);
          }
          for (let i = 0; i < videos.length; i += 1) {
            const v = videos[i];
            const fromName = v.filename?.split('.').pop()?.toLowerCase()
              || (v.blob instanceof File ? v.blob.name.split('.').pop()?.toLowerCase() : undefined);
            const mimeExt = v.blob.type.includes('quicktime')
              ? 'mov'
              : v.blob.type.includes('webm')
                ? 'webm'
                : 'mp4';
            const ext = fromName && ['mp4', 'mov', 'webm', 'm4v'].includes(fromName) ? fromName : mimeExt;
            const url = await uploadFile('post-photos', v.blob, `post-vid-${i + 1}.${ext}`);
            mediaUrls.push(url);
          }
          await db.posts.create({
            type: 'status',
            body: smack.trim() || undefined,
            photo_url: mediaUrls[0] ?? null,
            media_urls: mediaUrls,
            metadata: { tagged, guests },
          });
        } catch (err) {
          await Promise.all(mediaUrls.map((url) => deleteUploadedFile(url).catch(() => {})));
          throw err;
        }
        onPublished();
        onClose();
      } else if (mode === 'quick') {
        const gross = parseInt(quickScore, 10);
        if (!Number.isFinite(gross) || gross <= 0) throw new Error('Enter a valid total score');
        const postId = await publishRound(gross);
        onPublished();
        onClose();
        onRoundPublished?.(postId);
      } else {
        const postId = await publishRound(fullGross, holeScores);
        onPublished();
        onClose();
        onRoundPublished?.(postId);
      }
    } catch (err) {
      let message = err instanceof Error ? err.message : 'Unable to post. Check your connection and try again.';
      if (/row-level security|violates.*policy/i.test(message)) {
        message =
          'Post was blocked for this account. Try signing out and back in. If it persists, ask an admin to verify your login is linked to your member profile.';
      }
      setError(message || 'Unable to post. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const taggable = allMembers.filter((m) => m.id !== member.id && !tagged.includes(m.id));

  const tagPlayersBlock = (
    <>
      <div>
        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-mute">Tag Players</div>
        <div className="flex flex-wrap gap-1.5">
          {tagged.map((id) => {
            const m = allMembers.find((x) => x.id === id);
            return (
              <span key={id} className="inline-flex items-center gap-1 rounded-full bg-navy px-2.5 py-1 text-xs font-semibold text-cream">
                @{m?.handle}
                <button type="button" className="bt-flat" onClick={() => setTagged((t) => t.filter((x) => x !== id))}><X size={12} /></button>
              </span>
            );
          })}
          {taggable.slice(0, 8).map((m) => (
            <button key={m.id} type="button" onClick={() => setTagged((t) => [...t, m.id])} className="bt-flat rounded-full border border-dashed border-line px-2.5 py-1 text-xs text-ink-soft">
              + {m.name.split(' ')[0]}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-mute">Guests</div>
        <div className="flex gap-2">
          <input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Guest name" className="flex-1 rounded-xl border border-line bg-paper px-3 py-2 text-sm" />
          <button type="button" onClick={addGuest} className="rounded-xl bg-line px-3"><Plus size={16} /></button>
        </div>
        {guests.map((g, i) => (
          <span key={i} className="mr-2 mt-1 inline-block rounded-full bg-line px-2 py-1 text-xs">{g.name}</span>
        ))}
      </div>
    </>
  );

  const courseBlock = (
    <>
      <div>
        <div className="mb-2 text-[11px] font-bold tracking-wide text-mute">COURSE & TEES</div>
        <CourseTabPicker courses={courses} value={courseId} onChange={setCourseId} />
      </div>
      {tees.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tees.map((t) => (
            <button key={t.id} type="button" onClick={() => setTeeId(t.id)} className={`min-w-[60px] flex-1 rounded-sm px-2.5 py-2.5 text-xs font-semibold ${teeId === t.id ? 'border border-navy bg-navy text-cream' : 'bt-flat border border-line bg-paper text-ink-soft'}`}>{t.name}</button>
          ))}
        </div>
      )}
      <div>
        <div className="mb-1.5 text-[11px] font-bold tracking-wide text-mute">HOLES PLAYED</div>
        <div className="flex gap-2">
          {(['full', 'front-9', 'back-9'] as const).map((h) => (
            <button key={h} type="button" onClick={() => setHoles(h)} className={`flex-1 rounded-sm py-2.5 text-xs font-semibold ${holes === h ? 'border border-navy bg-navy text-cream' : 'bt-flat border border-line bg-paper text-ink-soft'}`}>
              {h === 'full' ? '18 Holes' : h === 'front-9' ? 'Front 9' : 'Back 9'}
            </button>
          ))}
        </div>
      </div>
      {course && tee && (
        <div className="font-mono text-xs text-mute">
          {tee.name} · Par {playedPar(holes, course.par, courseHoles)} · CR {tee.course_rating} · Slope {tee.slope} · Course HCP:{' '}
          <strong className="text-navy">{courseHandicap(member.handicap, tee.slope)}</strong>
        </div>
      )}
    </>
  );

  const smackBlock = (
    <div className="relative">
      <textarea
        rows={3}
        value={smack}
        onChange={(e) => setSmack(e.target.value)}
        placeholder={mode === 'photo' ? "What's the story?" : "How'd it go? Tell the club…"}
        className="w-full resize-none rounded-xl border border-line bg-paper px-3 py-2 pr-11 text-sm"
      />
      <button
        type="button"
        onClick={() => setShowEmoji((s) => !s)}
        className={`absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-lg text-base ${showEmoji ? 'bg-gold text-navy' : 'bg-cream text-ink-soft'}`}
      >
        😀
      </button>
      {showEmoji && (
        <EmojiPicker onPick={(e) => setSmack((s) => s + e)} onClose={() => setShowEmoji(false)} />
      )}
    </div>
  );

  const title = mode === 'photo' ? 'New Post' : 'Post a Round';

  return (
    <Sheet title={title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4 p-4 pb-8">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <div className="rounded border border-line bg-paper p-1">
          <div className="flex gap-1">
            <button type="button" onClick={() => setMode('photo')} className={`flex flex-1 items-center justify-center gap-1.5 rounded-sm py-2.5 text-[10px] font-bold tracking-wide ${mode === 'photo' ? 'bg-navy text-gold' : 'bt-flat text-ink-soft'}`}>
              <ImageIcon size={13} /> POST
            </button>
            <button type="button" onClick={() => setMode((m) => (m === 'photo' ? 'quick' : m))} className={`flex flex-1 items-center justify-center gap-1.5 rounded-sm py-2.5 text-[10px] font-bold tracking-wide ${mode !== 'photo' ? 'bg-navy text-gold' : 'bt-flat text-ink-soft'}`}>
              <Flag size={13} /> ROUND
            </button>
          </div>
        </div>

        {mode === 'photo' && (
          <p className="text-center text-[11px] leading-snug text-mute">Post anything — a thought or photos. No round required.</p>
        )}

        {mode !== 'photo' && (
          <div className="flex gap-1 rounded border border-line bg-paper p-1">
            <button type="button" onClick={() => setMode('quick')} className={`flex-1 rounded-sm py-2 text-[10px] font-bold tracking-wide ${mode === 'quick' ? 'bg-navy text-gold' : 'bt-flat text-ink-soft'}`}>
              QUICK SCORE
            </button>
            <button type="button" onClick={() => setMode('full')} className={`flex-1 rounded-sm py-2 text-[10px] font-bold tracking-wide ${mode === 'full' ? 'bg-navy text-gold' : 'bt-flat text-ink-soft'}`}>
              FULL SCORECARD
            </button>
          </div>
        )}

        {mode === 'photo' && (
          <div className="space-y-3">
            {!!photos.length && (
              <div className="grid grid-cols-2 gap-2">
                {photos.map((photo, idx) => (
                  <div key={photo.previewUrl} className="relative">
                    <img src={photo.previewUrl} alt="" className="h-36 w-full rounded-xl object-cover" />
                    <button
                      type="button"
                      onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== idx))}
                      className="absolute right-1.5 top-1.5 rounded-full bg-black/60 px-2 py-1 text-[10px] font-bold text-white"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            <ImageCropper
              label={photos.length ? 'Add more photos' : 'Add photos'}
              multiple
              hidePreview
              onCropped={() => {}}
              onCroppedMany={(items) => setPhotos((prev) => [...prev, ...items])}
            />
            <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-paper py-3 text-xs font-bold text-gold-deep">
              <input
                type="file"
                accept="video/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const previewUrl = URL.createObjectURL(file);
                  setVideos((prev) => [...prev, { blob: file, previewUrl, filename: file.name }]);
                  e.target.value = '';
                }}
              />
              Add video clip
            </label>
            {!!videos.length && videos.map((v, idx) => (
              <div key={v.previewUrl} className="relative">
                <video src={v.previewUrl} controls className="w-full rounded-xl" />
                <button type="button" onClick={() => setVideos((prev) => prev.filter((_, i) => i !== idx))} className="absolute right-1.5 top-1.5 rounded-full bg-black/60 px-2 py-1 text-[10px] font-bold text-white">Remove</button>
              </div>
            ))}
            {tagPlayersBlock}
            {smackBlock}
          </div>
        )}

        {mode === 'quick' && (
          <div className="space-y-4">
            {tagPlayersBlock}
            {courseBlock}
            <div>
              <label className="text-[11px] font-bold tracking-wide text-mute">TOTAL SCORE</label>
              <input type="number" value={quickScore} onChange={(e) => setQuickScore(e.target.value)} placeholder={String((course?.par || 72) + 8)} className="mt-1 w-full rounded-sm border border-line bg-paper px-3 py-3 text-center font-serif text-[32px] font-bold" />
            </div>
            <button type="button" onClick={() => setMode('full')} className="bt-flat w-full text-center text-xs font-semibold text-gold-deep">
              Enter hole-by-hole scorecard →
            </button>
            {smackBlock}
          </div>
        )}

        {mode === 'full' && (
          <div className="space-y-4">
            {tagPlayersBlock}
            {courseBlock}
            <div>
              <div className="mb-2 text-[11px] font-bold tracking-wide text-mute">HOLE-BY-HOLE</div>
              <GroupScorecard
                holes={playedHoles}
                course={course}
                teeSlope={tee?.slope}
                teeYardageTotal={tee?.yardage_total}
                players={players}
                scoresById={{
                  [member.id]: playedHoles.map((h) => holeScores[h.hole_num - 1]),
                  ...Object.fromEntries(
                    Object.entries(opponentScores).map(([pid, arr]) => [
                      pid,
                      playedHoles.map((h) => arr[h.hole_num - 1] ?? null),
                    ]),
                  ),
                }}
                puttsById={Object.fromEntries(
                  Object.entries(puttsById).map(([pid, arr]) => [
                    pid,
                    playedHoles.map((h) => arr[h.hole_num - 1] ?? null),
                  ]),
                )}
                onScore={(pid, hi, val) => {
                  const holeNum = playedHoles[hi]?.hole_num;
                  if (!holeNum) return;
                  const globalIdx = holeNum - 1;
                  if (pid === member.id) {
                    setHoleScores((prev) => {
                      const next = [...prev];
                      next[globalIdx] = val;
                      return next;
                    });
                  } else {
                    setOpponentScores((prev) => {
                      const cur = prev[pid] ? [...prev[pid]] : emptyScores(18);
                      cur[globalIdx] = val;
                      return { ...prev, [pid]: cur };
                    });
                  }
                }}
                onPutt={(pid, hi, val) => {
                  const holeNum = playedHoles[hi]?.hole_num;
                  if (!holeNum) return;
                  const globalIdx = holeNum - 1;
                  setPuttsById((prev) => {
                    const cur = prev[pid] ? [...prev[pid]] : emptyScores(18);
                    cur[globalIdx] = val;
                    return { ...prev, [pid]: cur };
                  });
                }}
                onHoleInOne={(holeNum) => onAce?.(holeNum, course?.name || 'the course')}
              />
            </div>
            <div>
              <div className="mb-2 text-[11px] font-bold tracking-wide text-mute">GAMES ON THIS ROUND</div>
              <GamesEditor
                games={composeGames}
                setGames={setComposeGames}
                members={allMembers.filter((m) => tagged.includes(m.id))}
                guests={guests.map((g, i) => ({ id: `guest-${i}`, name: g.name }))}
              />
            </div>
            <div>
              <div className="mb-2 text-[11px] font-bold tracking-wide text-mute">LIVE SIDE BETS</div>
              <LiveBetsPanel bets={liveBets} setBets={setLiveBets} members={allMembers} />
            </div>
            {smackBlock}
          </div>
        )}

        <button type="submit" disabled={!canPublish || submitting} className="w-full rounded-full bg-gold py-3 text-sm font-bold text-navy disabled:opacity-50">
          {submitting ? 'Posting…' : mode === 'photo' ? 'Post' : 'Post Round'}
        </button>
      </form>
    </Sheet>
  );
}
